import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Loader2, Search, User, Mail, ChevronDown, ChevronRight, MapPin, Phone, Briefcase, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactBadge = ({ contact, onEdit, onDelete }) => (
    <div className="flex items-center gap-3 p-3 bg-white border border-brand-gray-200 hover:border-brand-lime/40 transition-colors group" data-testid={`contact-badge-${contact.email}`}>
        <div className="h-9 w-9 bg-brand-lime/20 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-brand-charcoal">{contact.name?.charAt(0)?.toUpperCase() || '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-brand-charcoal truncate">{contact.name}</p>
            <div className="flex items-center gap-2 text-xs text-brand-gray-500">
                {contact.job_title && <span className="truncate">{contact.job_title}</span>}
                {contact.job_title && contact.email && <span>·</span>}
                <span className="truncate">{contact.email}</span>
            </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEdit(contact); }}>
                <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onDelete(contact); }}>
                <X className="h-3.5 w-3.5" />
            </Button>
        </div>
    </div>
);

const CustomerForm = ({ customer, onSave, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        name: customer?.name || '',
        address: customer?.address || '',
        phone: customer?.phone || '',
        contacts: customer?.contacts || [{ name: '', email: '', job_title: '' }]
    });

    const addContact = () => setFormData(prev => ({ ...prev, contacts: [...prev.contacts, { name: '', email: '', job_title: '' }] }));

    const removeContact = (index) => {
        if (formData.contacts.length <= 1) return;
        setFormData(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
    };

    const updateContact = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c)
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.address) {
            toast.error('Please fill in company name and address');
            return;
        }
        const validContacts = formData.contacts.filter(c => c.name && c.email);
        if (validContacts.length === 0) {
            toast.error('Please add at least one contact with name and email');
            return;
        }
        onSave({ ...formData, contacts: validContacts });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
            <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wide">Company Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Acme Industries" className="h-11" required data-testid="customer-name-input" />
            </div>
            <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wide">Address</Label>
                <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Full address" className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" required data-testid="customer-address-input" />
            </div>
            <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wide">Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+44..." className="h-11" data-testid="customer-phone-input" />
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold uppercase tracking-wide">Contacts ({formData.contacts.length})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1" /> Add Contact</Button>
                </div>
                {formData.contacts.map((contact, index) => (
                    <div key={contact.id || `new-contact-${index}`} className="p-3 border border-brand-gray-200 rounded-lg space-y-2 bg-brand-gray-50">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-brand-gray-500">Contact {index + 1}</span>
                            {formData.contacts.length > 1 && (
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(index)} className="h-6 w-6 text-red-500 hover:text-red-700"><X className="h-3.5 w-3.5" /></Button>
                            )}
                        </div>
                        <Input value={contact.name} onChange={(e) => updateContact(index, 'name', e.target.value)} placeholder="Contact Name" className="h-10" required data-testid={`contact-name-${index}`} />
                        <Input type="email" value={contact.email} onChange={(e) => updateContact(index, 'email', e.target.value)} placeholder="Email" className="h-10" required data-testid={`contact-email-${index}`} />
                        <Input value={contact.job_title} onChange={(e) => updateContact(index, 'job_title', e.target.value)} placeholder="Job Title (optional)" className="h-10" data-testid={`contact-title-${index}`} />
                    </div>
                ))}
            </div>
            <DialogFooter className="gap-2 pt-4">
                <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
                <Button type="submit" className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold" disabled={loading} data-testid="save-customer-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Customer'}
                </Button>
            </DialogFooter>
        </form>
    );
};

const CustomersManagement = () => {
    const { token } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [expandedCustomers, setExpandedCustomers] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);

    useEffect(() => { fetchCustomers(); }, []);

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
            setCustomers(response.data);
        } catch { toast.error('Failed to load customers'); }
        finally { setLoading(false); }
    };

    const handleSave = async (formData) => {
        setSaving(true);
        try {
            if (editingCustomer) {
                await axios.put(`${API}/admin/customers/${editingCustomer.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Customer updated');
            } else {
                await axios.post(`${API}/admin/customers`, formData, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Customer created');
            }
            setDialogOpen(false);
            setEditingCustomer(null);
            fetchCustomers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save customer');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!customerToDelete) return;
        setSaving(true);
        try {
            await axios.delete(`${API}/admin/customers/${customerToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Customer deleted');
            setDeleteConfirmOpen(false);
            setCustomerToDelete(null);
            fetchCustomers();
        } catch { toast.error('Failed to delete customer'); }
        finally { setSaving(false); }
    };

    const toggleExpanded = (id) => {
        setExpandedCustomers(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const filteredCustomers = customers.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.contacts?.some(ct => ct.name?.toLowerCase().includes(searchQuery.toLowerCase()) || ct.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Customers</h1>
                    <p className="text-brand-gray-500 mt-1">Manage companies and their contacts</p>
                </div>
                <Button onClick={() => { setEditingCustomer(null); setDialogOpen(true); }} className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold h-11 self-start sm:w-auto" data-testid="add-customer-btn">
                    <Plus className="h-5 w-5 mr-2" /> Add Customer
                </Button>
            </div>

            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                        <Input type="text" placeholder="Search companies, contacts, emails..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-11 pl-10" data-testid="customer-search" />
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredCustomers.length === 0 ? (
                        <div className="text-center py-12">
                            <Building2 className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                            <p className="text-brand-gray-500">No customers found</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredCustomers.map(customer => {
                                const isExpanded = expandedCustomers[customer.id];
                                const contactCount = customer.contacts?.length || 0;
                                return (
                                    <div key={customer.id} className="border border-brand-gray-200 hover:border-brand-lime/40 transition-colors" data-testid={`customer-card-${customer.id}`}>
                                        {/* Company header */}
                                        <div className="flex items-start gap-3 p-3 sm:p-4 cursor-pointer" onClick={() => toggleExpanded(customer.id)}>
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 bg-brand-lime/10 flex items-center justify-center flex-shrink-0">
                                                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-brand-lime" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-bold text-sm sm:text-base text-brand-charcoal truncate">{customer.name}</h3>
                                                    <span className="px-2 py-0.5 bg-brand-gray-100 text-brand-gray-600 text-xs rounded-full flex-shrink-0">
                                                        {contactCount} contact{contactCount !== 1 ? 's' : ''}
                                                    </span>
                                                    {!customer.active && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">Inactive</span>}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs sm:text-sm text-brand-gray-500 mt-1">
                                                    {customer.address && <span className="flex items-center gap-1 truncate"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate">{customer.address}</span></span>}
                                                </div>
                                                {/* Mobile action row */}
                                                <div className="flex items-center gap-2 mt-2 sm:hidden">
                                                    <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={(e) => { e.stopPropagation(); setEditingCustomer(customer); setDialogOpen(true); }}>
                                                        <Pencil className="h-3 w-3 mr-1" /> Edit
                                                    </Button>
                                                    <Button variant="outline" size="sm" className="h-7 text-xs px-2 text-red-500 border-red-200" onClick={(e) => { e.stopPropagation(); setCustomerToDelete(customer); setDeleteConfirmOpen(true); }}>
                                                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                                                    </Button>
                                                    {isExpanded ? <ChevronDown className="h-4 w-4 text-brand-gray-400 ml-auto" /> : <ChevronRight className="h-4 w-4 text-brand-gray-400 ml-auto" />}
                                                </div>
                                            </div>
                                            {/* Desktop actions */}
                                            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditingCustomer(customer); setDialogOpen(true); }} data-testid={`edit-customer-${customer.id}`}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setCustomerToDelete(customer); setDeleteConfirmOpen(true); }} data-testid={`delete-customer-${customer.id}`}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                                {isExpanded ? <ChevronDown className="h-5 w-5 text-brand-gray-400" /> : <ChevronRight className="h-5 w-5 text-brand-gray-400" />}
                                            </div>
                                        </div>

                                        {/* Expanded contacts */}
                                        {isExpanded && customer.contacts && customer.contacts.length > 0 && (
                                            <div className="border-t border-brand-gray-200 p-3 sm:p-4 bg-brand-gray-50/50">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                    {customer.contacts.map((contact) => (
                                                        <ContactBadge
                                                            key={contact.id || contact.email}
                                                            contact={contact}
                                                            onEdit={() => { setEditingCustomer(customer); setDialogOpen(true); }}
                                                            onDelete={() => {
                                                                if (customer.contacts.length <= 1) {
                                                                    toast.error('Cannot remove the last contact');
                                                                    return;
                                                                }
                                                                const updatedContacts = customer.contacts.filter((_, i) => i !== idx);
                                                                axios.put(`${API}/admin/customers/${customer.id}`, { contacts: updatedContacts }, { headers: { Authorization: `Bearer ${token}` } })
                                                                    .then(() => { toast.success('Contact removed'); fetchCustomers(); })
                                                                    .catch(() => toast.error('Failed to remove contact'));
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Collapsed preview - show first 3 contacts inline */}
                                        {!isExpanded && contactCount > 0 && (
                                            <div className="border-t border-brand-gray-100 px-4 py-2 flex items-center gap-2 flex-wrap">
                                                {customer.contacts.slice(0, 3).map((contact) => (
                                                    <span key={contact.id || contact.email} className="inline-flex items-center gap-1.5 px-2 py-1 bg-brand-gray-100 text-xs text-brand-gray-600 rounded-full">
                                                        <User className="h-3 w-3" /> {contact.name}
                                                    </span>
                                                ))}
                                                {contactCount > 3 && (
                                                    <span className="text-xs text-brand-gray-400">+{contactCount - 3} more</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[550px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
                        <DialogDescription>{editingCustomer ? 'Update company and contact details' : 'Add a new company and their contacts'}</DialogDescription>
                    </DialogHeader>
                    <CustomerForm customer={editingCustomer} onSave={handleSave} onCancel={() => setDialogOpen(false)} loading={saving} />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Customer</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{customerToDelete?.name}"? This action cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-customer-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CustomersManagement;
